import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { ChevronRight, ChevronLeft, Search, X } from "lucide-react";
import Loading from "react-loading";
import SessionUpdateBtn from "../../components/SessionUpdateBtn";
import AppSidebar from "../../components/AppSidebar";
import axios from "../../common/axios";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "../../components/ui/sidebar";
import { Separator } from "../../components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import "../list.css";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const s = String(dateStr).trim();
  const match = s.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (match) return `${match[1]}\n${match[2]}`;
  return s;
};

const validTabs = ["pending", "draft", "done", "inform"];

const LoaList = ({ bpName, title }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [filters, setFilters] = useState({});
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [approvalList, setApprovalList] = useState([]);
  const [totalList, setTotalList] = useState([]);
  const [draftList, setDraftList] = useState([]);
  const [informList, setInformList] = useState([]);
  const [activeTab, setActiveTab] = useState(() => {
    const initialTab = searchParams.get("tab");
    return validTabs.includes(initialTab) ? initialTab : "pending";
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [errSnackBarOpen, setErrSnackBarOpen] = useState(false);
  const [errSnackBarMsg, setErrSnackBarMsg] = useState("");
  const unifierUrl = process.env.REACT_APP_UNIFIER_URL;

  const isAllView = bpName === "" || bpName == null;

  useEffect(() => {
    const controller = new AbortController();

    setIsLoading(true);

    const fetchData = async () => {
      try {
        const requestLoaList = await axios.get(
          `/loa/list?bpName=${bpName ?? ""}`,
          { signal: controller.signal },
        );
        const {
          myTurnList = [],
          othersList = [],
          draftList = [],
          informList = [],
        } = requestLoaList.data;
        setApprovalList(myTurnList);
        setTotalList(othersList);
        setDraftList(draftList);
        setInformList(informList);
      } catch (err) {
        if (err.code === "ERR_CANCELED") return;
        let errorMessage =
          err.response?.data?.message || "데이터 호출에 실패하였습니다.";
        if (err.response?.status === 401) errorMessage = "권한이 없습니다.";
        setErrSnackBarOpen(true);
        setErrSnackBarMsg(errorMessage);
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };

    fetchData();

    return () => {
      controller.abort();
    };
  }, [bpName]);

  useEffect(() => {
    setSearchValue("");
    setFilters({});
    setSortField(null);
    setSortDir("asc");
    setPage(1);
  }, [bpName]);

  const handleSort = (field) => {
    setPage(1);
    if (sortField !== field) {
      setSortField(field);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortField(null);
      setSortDir("asc");
    }
  };

  const columnList = useMemo(() => {
    if (bpName === "Vendors Evaluation Approval")
      return vendorEvaluationColumnList;
    if (isAllView) return allColumnList;
    return defaultColumnList;
  }, [bpName, isAllView]);

  const activeBaseList = useMemo(() => {
    if (activeTab === "pending") return approvalList;
    if (activeTab === "draft") return draftList;
    if (activeTab === "inform") return informList;
    if (activeTab === "done") return totalList;

    return [];
  }, [activeTab, approvalList, draftList, totalList, informList]);

  const getDisplayValue = (c, field) => {
    const raw = c[field];
    if (field === "date") return formatDate(raw ?? "");
    return (raw ?? "").toString();
  };

  const filteredList = useMemo(() => {
    return activeBaseList.filter((c) => {
      const sv = searchValue.trim().toLowerCase();
      if (sv) {
        const matchSearch = columnList.some(({ field }) =>
          getDisplayValue(c, field).toLowerCase().includes(sv),
        );
        if (!matchSearch) return false;
      }
      return columnList.every(({ field }) => {
        const fv = (filters[field] ?? "").toString().trim().toLowerCase();
        if (!fv) return true;
        return getDisplayValue(c, field).toLowerCase().includes(fv);
      });
    });
  }, [activeBaseList, searchValue, filters, columnList]);

  const sortedList = useMemo(() => {
    if (!sortField) return filteredList;
    return [...filteredList].sort((a, b) => {
      const av = (a[sortField] ?? "").toString();
      const bv = (b[sortField] ?? "").toString();
      const cmp = av.localeCompare(bv, "ko", { numeric: true });
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filteredList, sortField, sortDir]);

  const chunkedList = useMemo(() => {
    if (pageSize === "all") return [sortedList];
    return chunk(sortedList, pageSize);
  }, [sortedList, pageSize]);

  const totalPage = chunkedList.length;

  const pageWindow = useMemo(() => {
    const siblings = 1;
    const range = [];
    const start = Math.max(2, page - siblings);
    const end = Math.min(totalPage - 1, page + siblings);

    range.push(1);
    if (start > 2) range.push("start-ellipsis");
    for (let i = start; i <= end; i++) range.push(i);
    if (end < totalPage - 1) range.push("end-ellipsis");
    if (totalPage > 1) range.push(totalPage);

    return range;
  }, [page, totalPage]);

  const handleRouter = (project_code, record_no, itemBpName) => {
    navigate(
      `${pathname}/detail?bpName=${itemBpName ?? bpName ?? ""}&project_code=${
        project_code ?? ""
      }&record_no=${record_no ?? ""}&tab=${activeTab}`,
    );
  };

  const tabList = [
    { value: "pending", label: "미결함", count: approvalList.length },
    { value: "draft", label: "상신함", count: draftList.length },
    { value: "done", label: "기결함", count: totalList.length },
    { value: "inform", label: "통보함", count: informList.length },
  ];

  const switchTab = (value) => {
    setActiveTab(value);
    setPage(1);
    setSearchValue("");
    setFilters({});
    setSortField(null);
    setSortDir("asc");
    const next = new URLSearchParams(searchParams);
    next.set("tab", value);
    setSearchParams(next, { replace: true });
  };

  const closeSnackBar = () => {
    setErrSnackBarOpen(false);
    setErrSnackBarMsg("");
  };

  useEffect(() => {
    if (!errSnackBarOpen) return;
    const timer = setTimeout(closeSnackBar, 15000);
    return () => clearTimeout(timer);
  }, [errSnackBarOpen]);

  const renderRow = (c) => {
    const { id, project_code, record_no, bpName: itemBpName } = c;
    const rowKey =
      id ?? `${project_code ?? "unknown"}-${record_no ?? "unknown"}`;
    return (
      <div
        className="list-body-row"
        key={rowKey}
        onClick={() => handleRouter(project_code, record_no, itemBpName)}
      >
        {columnList.map((c2) => {
          const { field, width, textPosition } = c2;
          const raw = c[field];
          const display = field === "date" ? formatDate(raw) : (raw ?? "");
          const isEmpty = raw == null || raw === "";
          return (
            <div
              key={field}
              className={`list-body-row-cell${field === "date" ? " list-date-cell" : ""}${field === "title" ? " list-title-cell" : ""}`}
              style={{ width, textAlign: textPosition }}
            >
              {field === "vendor_name" && isEmpty ? "N/A" : display}
            </div>
          );
        })}
      </div>
    );
  };

  const topbarJsx = (
    <header className="shrink-0 flex items-center gap-2 h-12 px-3 border-b border-[#e4e4e7] bg-white">
      <SidebarTrigger />
      <Separator orientation="vertical" className="h-4" />
      <Breadcrumb className="min-w-0 overflow-hidden">
        <BreadcrumbList className="flex-nowrap overflow-hidden">
          <BreadcrumbItem>LOA</BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem className="min-w-0 overflow-hidden">
            <BreadcrumbPage className="truncate">{title}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <div className="ml-auto flex items-center gap-2 shrink-0">
        <Button
          variant="outline"
          size="sm"
          asChild
          className="hidden md:inline-flex"
        >
          <a
            href={unifierUrl || "#"}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => {
              if (!unifierUrl) e.preventDefault();
            }}
          >
            유니파이어 바로가기
          </a>
        </Button>
        <SessionUpdateBtn />
      </div>
    </header>
  );

  return (
    <SidebarProvider defaultOpen>
      <AppSidebar bpName={bpName} />
      <SidebarInset className="h-svh flex flex-col overflow-hidden">
        {topbarJsx}
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
          {isLoading ? (
            <div
              className="total-list-body"
              style={{ justifyContent: "center" }}
            >
              <Loading color="#007bff" />
            </div>
          ) : (
            <div className="total-list-body">
              <div className="list-body">
                <div className="total-list-box">
                  <div className="list-toolbar-header">
                    <div
                      role="tablist"
                      aria-label="품의 목록 탭"
                      className="flex flex-wrap gap-1.5 self-stretch"
                      onKeyDown={(e) => {
                        const idx = tabList.findIndex(
                          (t) => t.value === activeTab,
                        );
                        let nextIdx = null;
                        if (e.key === "ArrowRight") {
                          nextIdx = (idx + 1) % tabList.length;
                        } else if (e.key === "ArrowLeft") {
                          nextIdx = (idx - 1 + tabList.length) % tabList.length;
                        } else if (e.key === "Home") {
                          nextIdx = 0;
                        } else if (e.key === "End") {
                          nextIdx = tabList.length - 1;
                        }
                        if (nextIdx !== null) {
                          e.preventDefault();
                          const nextTab = tabList[nextIdx];
                          switchTab(nextTab.value);
                          requestAnimationFrame(() => {
                            document
                              .getElementById(`loa-list-tab-${nextTab.value}`)
                              ?.focus();
                          });
                        }
                      }}
                    >
                      {tabList.map((t) => (
                        <Button
                          key={t.value}
                          type="button"
                          role="tab"
                          aria-selected={activeTab === t.value}
                          aria-controls="loa-list-tabpanel"
                          id={`loa-list-tab-${t.value}`}
                          tabIndex={activeTab === t.value ? 0 : -1}
                          variant={
                            activeTab === t.value ? "default" : "outline"
                          }
                          size="sm"
                          className={`rounded-full ${
                            activeTab === t.value ? "" : "text-muted-foreground"
                          }`}
                          onClick={() => switchTab(t.value)}
                        >
                          {t.label} ({t.count})
                        </Button>
                      ))}
                    </div>
                    <div className="list-search-box relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-muted-foreground pointer-events-none" />
                      <Input
                        type="text"
                        placeholder="검색..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        className="pl-9 text-[13px] bg-muted w-full"
                      />
                    </div>
                  </div>
                  <div
                    id="loa-list-tabpanel"
                    role="tabpanel"
                    aria-labelledby={`loa-list-tab-${activeTab}`}
                    className="list-table-scroll"
                  >
                    <div className="list-count-box">
                      <div className="list-count-text">총</div>
                      <div className="list-count-text-box">
                        <div className="list-count-number">
                          {filteredList.length.toLocaleString()}
                        </div>
                        <div className="list-count-text">건</div>
                      </div>
                    </div>
                    <div className="list-header-row">
                      {columnList.map(({ field, label, width }) => (
                        <div
                          key={field}
                          className="list-header-row-cell"
                          style={{ width }}
                        >
                          <div
                            className="list-header-label list-header-sortable"
                            onClick={() => handleSort(field)}
                          >
                            {label}
                            <span className="list-sort-icon">
                              {sortField === field
                                ? sortDir === "asc"
                                  ? " ▲"
                                  : " ▼"
                                : " ⇅"}
                            </span>
                          </div>
                          <input
                            className="list-filter-input"
                            placeholder="필터..."
                            value={filters[field] ?? ""}
                            onChange={(e) =>
                              setFilters((prev) => ({
                                ...prev,
                                [field]: e.target.value,
                              }))
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="list-body-box">
                      {(chunkedList[page - 1] ?? []).map(renderRow)}
                    </div>
                  </div>
                </div>
                <div className="list-pagivation-box">
                  {totalPage > 0 && (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        disabled={page <= 1}
                        onClick={() => setPage(page - 1)}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      {pageWindow.map((p) =>
                        typeof p === "number" ? (
                          <Button
                            key={p}
                            variant={p === page ? "default" : "ghost"}
                            size="icon"
                            className="h-8 w-8 text-xs"
                            onClick={() => setPage(p)}
                          >
                            {p}
                          </Button>
                        ) : (
                          <span
                            key={p}
                            className="h-8 w-8 flex items-center justify-center text-xs text-muted-foreground"
                          >
                            …
                          </span>
                        ),
                      )}
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        disabled={page >= totalPage}
                        onClick={() => setPage(page + 1)}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      const v =
                        e.target.value === "all"
                          ? "all"
                          : Number(e.target.value);
                      setPageSize(v);
                      setPage(1);
                    }}
                    className="absolute right-0 h-8 min-w-[90px] rounded-md border border-input bg-background px-2 text-[13px]"
                  >
                    <option value="all">전체</option>
                    <option value={10}>10</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={1000}>1000</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </SidebarInset>
      {errSnackBarOpen && (
        <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-md bg-[#323232] px-4 py-3 text-sm text-white shadow-lg">
          <span>{errSnackBarMsg}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-white hover:bg-white/10 hover:text-white"
            aria-label="close"
            onClick={closeSnackBar}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </SidebarProvider>
  );
};

export default LoaList;

const chunk = (arr, size = 15) => {
  const result = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

const defaultColumnList = [
  { field: "id", label: "번호", width: "8%", textPosition: "center" },
  {
    field: "project_name",
    label: "프로젝트명",
    width: "18%",
    textPosition: "start",
  },
  { field: "title", label: "제목", width: "44%", textPosition: "start" },
  {
    field: "vendor_name",
    label: "업체명",
    width: "15%",
    textPosition: "start",
  },
  { field: "status_kr", label: "상태", width: "15%", textPosition: "start" },
  { field: "date", label: "날짜", width: "15%", textPosition: "start" },
];

const allColumnList = [
  { field: "id", label: "번호", width: "7%", textPosition: "center" },
  {
    field: "project_name",
    label: "프로젝트명",
    width: "15%",
    textPosition: "start",
  },
  { field: "bp_label", label: "결재구분", width: "15%", textPosition: "start" },
  { field: "title", label: "제목", width: "33%", textPosition: "start" },
  {
    field: "vendor_name",
    label: "업체명",
    width: "15%",
    textPosition: "start",
  },
  { field: "status_kr", label: "상태", width: "15%", textPosition: "start" },
  { field: "date", label: "날짜", width: "15%", textPosition: "start" },
];

const vendorEvaluationColumnList = [
  { field: "id", label: "번호", width: "15%", textPosition: "center" },
  { field: "title", label: "제목", width: "65%", textPosition: "start" },
  { field: "date", label: "날짜", width: "20%", textPosition: "start" },
];
